/**
 * Zentraler Ablauf-Sweep: LOT → EXPIRED, STOCK_MOVEMENT EXPIRY, Bestand aus Lots.
 * Läuft beim App-Start, POS und Inventar — nicht nur auf der Inventar-Seite.
 * ADR-0013 Layout: öffentliche API = domain/inventory/expirySweep.
 */

import { getCurrentBusinessId } from '../../../utils/sharedCore';
import { LOT_STATUS, isLotExpired } from '../../../utils/lotSystem';
import { processExpiredLotsSweep } from '../../../utils/expiredProductsManager';
import { mergeExpiredProductsForDisplay } from '../expiredView/expiredLotsView';
import { syncProductQuantityProjections } from '../projections/productQuantityProjection';

async function loadExpirySweepContext(businessId) {
  const { readProductsFromBestSource } = await import('../../../utils/productsReadSource');
  const { readPurchasesFromBestSource } = await import('../../../utils/purchasesReadSource');
  const { readSalesFromBestSource } = await import('../../../utils/salesReadSource');
  const { readLotsFromBestSource } = await import('../../../utils/lotsReadSource');
  const { readExpiredProductsFromBestSource } = await import(
    '../../../utils/expiredProductsReadSource'
  );

  const [products, purchases, sales, lots, legacyExpired] = await Promise.all([
    readProductsFromBestSource(businessId),
    readPurchasesFromBestSource(businessId),
    readSalesFromBestSource(businessId),
    readLotsFromBestSource(businessId),
    readExpiredProductsFromBestSource(businessId)
  ]);

  return {
    products: Array.isArray(products) ? products : [],
    purchases: Array.isArray(purchases) ? purchases : [],
    sales: Array.isArray(sales) ? sales : [],
    lots: Array.isArray(lots) ? lots : [],
    legacyExpired: Array.isArray(legacyExpired) ? legacyExpired : []
  };
}

/**
 * @returns {Promise<{ processedCount: number, movementCount: number, derivedExpired: object[] }>}
 */
export async function runExpirySweep(businessId = null) {
  const bid = businessId || getCurrentBusinessId({ allowFallback: true });
  if (!bid) {
    return { processedCount: 0, movementCount: 0, derivedExpired: [] };
  }

  const ctx = await loadExpirySweepContext(bid);
  if (!ctx.products.length && !ctx.lots.length) {
    return { processedCount: 0, movementCount: 0, derivedExpired: [] };
  }

  const result = await processExpiredLotsSweep({
    businessId: bid,
    purchases: ctx.purchases,
    products: ctx.products,
    sales: ctx.sales,
    lots: ctx.lots
  });

  const touchedIds = result.processedProductIds || [];
  if (touchedIds.length > 0) {
    await syncProductQuantityProjections(bid, touchedIds);
  }

  const { readLotsFromBestSource } = await import('../../../utils/lotsReadSource');
  const { readProductsFromBestSource } = await import('../../../utils/productsReadSource');
  const { readExpiredProductsFromBestSource } = await import(
    '../../../utils/expiredProductsReadSource'
  );

  const [freshLots, freshProducts, freshLegacyExpired] = await Promise.all([
    readLotsFromBestSource(bid),
    readProductsFromBestSource(bid),
    readExpiredProductsFromBestSource(bid)
  ]);

  const derivedExpired = mergeExpiredProductsForDisplay(
    freshLegacyExpired.length ? freshLegacyExpired : ctx.legacyExpired,
    Array.isArray(freshLots) ? freshLots : ctx.lots,
    Array.isArray(freshProducts) ? freshProducts : ctx.products
  );

  return {
    processedCount: result.processedCount || 0,
    movementCount: result.movementCount || 0,
    derivedExpired,
    processedProductIds: touchedIds
  };
}

/**
 * Schnellprüfung: gibt es Lots die abgelaufen und noch nicht als EXPIRED verbucht sind?
 */
export function hasLotsDueForExpiry(lots = []) {
  const list = Array.isArray(lots) ? lots : [];
  return list.some((lot) => {
    if (!lot?.expirationDate) return false;
    if (lot.status === LOT_STATUS.EXPIRED) return false;
    if (Number(lot.remainingQuantity) <= 0) return false;
    return isLotExpired(lot);
  });
}
